import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Plus, Trash2, Edit3, Save, X, Package, ShoppingBag, Sliders, Users as UsersIcon, Filter, AlertTriangle } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import Layout from "../components/Layout";
import ContentEditor from "../components/ContentEditor";
import UsersAdmin from "../components/UsersAdmin";

const emptyCase = {
  slug: "",
  code: "Caso #",
  title: "",
  subtitle: "",
  summary: "",
  full_description: "",
  location: "",
  year: "",
  duration: "3-4 horas",
  players: "2-6 jogadores",
  difficulty: "Intermédio",
  price: 14.99,
  currency: "eur",
  cover_image: "",
  gallery: [],
  suspects: [],
  evidence: [],
  clues: [],
  status: "available",
  release_date: "",
  featured: false,
  pdf_url: "",
  age_rating: "14+",
  document_count: 25,
  made_in: "Portugal",
  victim: null,
};

export default function AdminDashboard() {
  const { admin, logout, loading } = useAuth();
  const nav = useNavigate();
  const [cases, setCases] = useState([]);
  const [orders, setOrders] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [tab, setTab] = useState("cases");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  // Orders tab filters
  const [orderStatusFilter, setOrderStatusFilter] = useState("paid"); // paid | pending | all
  const [hideTestOrders, setHideTestOrders] = useState(true);
  const [cleaningTests, setCleaningTests] = useState(false);

  useEffect(() => {
    if (!loading && !admin) nav("/admin/login");
  }, [admin, loading, nav]);

  const fetchOrders = async () => {
    try {
      const params = new URLSearchParams({
        payment_status_filter: orderStatusFilter,
        hide_test: String(hideTestOrders),
      });
      const r = await api.get(`/admin/orders?${params.toString()}`);
      setOrders(r.data);
    } catch (_) { /* noop */ }
  };

  useEffect(() => {
    if (admin) {
      api.get("/cases").then((r) => setCases(r.data));
      fetchOrders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin, orderStatusFilter, hideTestOrders]);

  const cleanupTestData = async () => {
    // 1) Dry run preview
    setCleaningTests(true);
    try {
      const preview = await api.post("/admin/cleanup-test-data?dry_run=true", {});
      const n = preview.data.would_delete;
      if (n === 0) {
        setMsg("Nada para limpar — sem dados de teste.");
        setTimeout(() => setMsg(""), 4000);
        return;
      }
      if (!window.confirm(
        `Apagar ${n} transações de teste?\n\n` +
        `Serão removidas:\n` +
        `• Encomendas PENDING / abandonadas\n` +
        `• Compras com valor entre 0€ e 5€ (cartões de teste)\n\n` +
        `Compras reais (>= 5€ PAID) e acessos manuais NÃO serão tocados.`
      )) return;
      const r = await api.post("/admin/cleanup-test-data", {});
      setMsg(`${r.data.deleted} transações de teste removidas.`);
      setTimeout(() => setMsg(""), 5000);
      await fetchOrders();
    } catch (e) {
      setMsg(e.response?.data?.detail || "Erro ao limpar dados.");
      setTimeout(() => setMsg(""), 5000);
    } finally {
      setCleaningTests(false);
    }
  };

  const reload = async () => {
    const r = await api.get("/cases");
    setCases(r.data);
  };

  const save = async (data) => {
    setBusy(true);
    setMsg("");
    try {
      if (data.id) {
        await api.put(`/admin/cases/${data.id}`, data);
        setMsg("Caso atualizado.");
      } else {
        await api.post("/admin/cases", data);
        setMsg("Caso criado.");
      }
      setEditing(null);
      setCreating(false);
      await reload();
    } catch (e) {
      setMsg(e.response?.data?.detail || "Erro ao guardar");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Apagar este caso?")) return;
    await api.delete(`/admin/cases/${id}`);
    await reload();
  };

  if (loading || !admin) return null;

  return (
    <Layout noFooter>
      <section className="py-14">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between mb-10 flex-wrap gap-4">
            <div>
              <p className="section-tag mb-3">Painel de controlo</p>
              <h1 className="headline text-4xl md:text-5xl">Administração</h1>
              <p className="mono text-[11px] text-bone-muted tracking-[0.2em] mt-2">
                {admin.email}
              </p>
            </div>
            <button
              data-testid="admin-logout"
              onClick={() => { logout(); nav("/"); }}
              className="btn-ghost"
            >
              <LogOut className="w-4 h-4" /> Sair
            </button>
          </div>

          <div className="flex gap-px bg-white/10 mb-8 w-fit flex-wrap">
            <button
              onClick={() => setTab("cases")}
              data-testid="admin-tab-cases"
              className={`px-6 py-3 mono text-[11px] tracking-[0.25em] ${tab === "cases" ? "bg-blood text-white" : "bg-ink-100 text-bone-muted hover:text-white"}`}
            >
              <Package className="w-3.5 h-3.5 inline mr-2" /> CASOS ({cases.length})
            </button>
            <button
              onClick={() => setTab("content")}
              data-testid="admin-tab-content"
              className={`px-6 py-3 mono text-[11px] tracking-[0.25em] ${tab === "content" ? "bg-blood text-white" : "bg-ink-100 text-bone-muted hover:text-white"}`}
            >
              <Sliders className="w-3.5 h-3.5 inline mr-2" /> CONTEÚDO DO SITE
            </button>
            <button
              onClick={() => setTab("orders")}
              data-testid="admin-tab-orders"
              className={`px-6 py-3 mono text-[11px] tracking-[0.25em] ${tab === "orders" ? "bg-blood text-white" : "bg-ink-100 text-bone-muted hover:text-white"}`}
            >
              <ShoppingBag className="w-3.5 h-3.5 inline mr-2" /> ENCOMENDAS ({orders.length})
            </button>
            <button
              onClick={() => setTab("users")}
              data-testid="admin-tab-users"
              className={`px-6 py-3 mono text-[11px] tracking-[0.25em] ${tab === "users" ? "bg-blood text-white" : "bg-ink-100 text-bone-muted hover:text-white"}`}
            >
              <UsersIcon className="w-3.5 h-3.5 inline mr-2" /> UTILIZADORES
            </button>
          </div>

          {msg && <p className="mono text-xs text-blood tracking-[0.2em] mb-4">{msg}</p>}

          {tab === "content" && <ContentEditor />}

          {tab === "cases" && (
            <>
              {!creating && !editing && (
                <button
                  onClick={() => setCreating(true)}
                  data-testid="admin-new-case-btn"
                  className="btn-primary mb-6"
                >
                  <Plus className="w-4 h-4" /> Novo caso
                </button>
              )}

              {(creating || editing) && (
                <CaseForm
                  initial={editing || emptyCase}
                  onCancel={() => { setEditing(null); setCreating(false); }}
                  onSave={save}
                  busy={busy}
                />
              )}

              <div className="space-y-px bg-white/10 mt-6">
                {cases.map((c) => (
                  <div key={c.id} className="bg-ink-100 p-5 flex items-center gap-5">
                    <div
                      className="w-16 h-20 bg-cover bg-center shrink-0"
                      style={{ backgroundImage: `url(${c.cover_image})` }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="mono text-[10px] text-blood tracking-[0.3em] mb-1">{c.code}</p>
                      <h3 className="headline text-xl truncate">{c.title}</h3>
                      <p className="text-xs text-bone-muted truncate mt-1">{c.subtitle}</p>
                    </div>
                    <div className="mono text-[10px] tracking-[0.2em] text-bone-faded hidden md:block">
                      {c.status.toUpperCase()} · {c.price.toFixed(2)}€
                    </div>
                    <button
                      onClick={() => setEditing(c)}
                      data-testid={`admin-edit-${c.slug}`}
                      className="p-2.5 border border-white/10 hover:border-white/30 transition-colors"
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => remove(c.id)}
                      data-testid={`admin-delete-${c.slug}`}
                      className="p-2.5 border border-white/10 hover:border-blood hover:text-blood transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}

          {tab === "orders" && (
            <>
              <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
                <div className="flex items-center gap-2 flex-wrap">
                  <Filter className="w-3.5 h-3.5 text-bone-faded" />
                  {[
                    { v: "paid", label: "PAID" },
                    { v: "pending", label: "PENDING" },
                    { v: "all", label: "TODOS" },
                  ].map((f) => (
                    <button
                      key={f.v}
                      data-testid={`orders-filter-${f.v}`}
                      onClick={() => setOrderStatusFilter(f.v)}
                      className={`mono text-[10px] tracking-[0.25em] px-3 py-2 border ${
                        orderStatusFilter === f.v
                          ? "border-blood bg-blood text-white"
                          : "border-white/15 text-bone-muted hover:text-white hover:border-white/40"
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                  <label
                    data-testid="orders-hide-test-toggle"
                    className="ml-3 inline-flex items-center gap-2 mono text-[10px] tracking-[0.25em] text-bone-muted hover:text-white cursor-pointer select-none"
                  >
                    <input
                      type="checkbox"
                      checked={hideTestOrders}
                      onChange={(e) => setHideTestOrders(e.target.checked)}
                      className="accent-blood"
                    />
                    OCULTAR ENCOMENDAS DE TESTE
                  </label>
                </div>
                <button
                  data-testid="orders-cleanup-btn"
                  onClick={cleanupTestData}
                  disabled={cleaningTests}
                  className="inline-flex items-center gap-2 mono text-[10px] tracking-[0.25em] text-blood border border-blood/40 hover:bg-blood/10 px-3 py-2 transition-colors disabled:opacity-50"
                  title="Apaga PENDING e 1€ de teste, preserva reais e manuais"
                >
                  <AlertTriangle className="w-3.5 h-3.5" /> LIMPAR DADOS DE TESTE
                </button>
              </div>
              <p data-testid="orders-count" className="mono text-[10px] tracking-[0.25em] text-bone-faded mb-3">
                {orders.length} ENCOMENDA{orders.length === 1 ? "" : "S"}
              </p>
              <div className="space-y-px bg-white/10">
                {orders.length === 0 && (
                  <p className="bg-ink-100 p-8 text-bone-muted mono text-sm tracking-[0.2em]">SEM ENCOMENDAS</p>
                )}
                {orders.map((o) => (
                  <div key={o.session_id} className="bg-ink-100 p-5 grid md:grid-cols-6 gap-4 items-center">
                    <div className="md:col-span-2">
                      <p className="mono text-[10px] tracking-[0.2em] text-bone-faded">SESSÃO</p>
                      <p className="mono text-xs truncate">{o.session_id?.slice(0, 24)}...</p>
                      {o.source === "manual_grant" && (
                        <span className="mono text-[9px] text-blood tracking-[0.25em]">MANUAL</span>
                      )}
                    </div>
                    <div>
                      <p className="mono text-[10px] tracking-[0.2em] text-bone-faded">CASO</p>
                      <p className="text-sm truncate">{o.case_title}</p>
                    </div>
                    <div>
                      <p className="mono text-[10px] tracking-[0.2em] text-bone-faded">EMAIL</p>
                      <p className="text-xs truncate">{o.customer_email || "-"}</p>
                    </div>
                    <div>
                      <p className="mono text-[10px] tracking-[0.2em] text-bone-faded">VALOR</p>
                      <p className="text-sm">{o.amount?.toFixed(2)}€</p>
                    </div>
                    <div>
                      <p className="mono text-[10px] tracking-[0.2em] text-bone-faded">STATUS</p>
                      <p className={`mono text-xs uppercase tracking-[0.2em] ${o.payment_status === "paid" ? "text-blood" : "text-bone-muted"}`}>
                        {o.payment_status}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {tab === "users" && <UsersAdmin allCases={cases} />}
        </div>
      </section>
    </Layout>
  );
}

function CaseForm({ initial, onCancel, onSave, busy }) {
  const [form, setForm] = useState(initial);

  const setField = (k, v) => setForm({ ...form, [k]: v });

  const setJsonField = (k, txt) => {
    try {
      setForm({ ...form, [k]: JSON.parse(txt) });
    } catch {
      // ignore until valid
    }
  };

  return (
    <div className="dossier p-6 md:p-8 mb-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="headline text-2xl">{initial.id ? "Editar caso" : "Novo caso"}</h3>
        <button onClick={onCancel} className="text-bone-muted hover:text-white" data-testid="admin-form-cancel">
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        <Field label="Slug (URL)"><input data-testid="admin-form-slug" value={form.slug} onChange={(e) => setField("slug", e.target.value)} className="cec-input" /></Field>
        <Field label="Código"><input data-testid="admin-form-code" value={form.code} onChange={(e) => setField("code", e.target.value)} className="cec-input" /></Field>
        <Field label="Título"><input data-testid="admin-form-title" value={form.title} onChange={(e) => setField("title", e.target.value)} className="cec-input" /></Field>
        <Field label="Subtítulo"><input value={form.subtitle} onChange={(e) => setField("subtitle", e.target.value)} className="cec-input" /></Field>
        <Field label="Local"><input value={form.location} onChange={(e) => setField("location", e.target.value)} className="cec-input" /></Field>
        <Field label="Ano"><input value={form.year} onChange={(e) => setField("year", e.target.value)} className="cec-input" /></Field>
        <Field label="Duração"><input value={form.duration} onChange={(e) => setField("duration", e.target.value)} className="cec-input" /></Field>
        <Field label="Jogadores"><input value={form.players} onChange={(e) => setField("players", e.target.value)} className="cec-input" /></Field>
        <Field label="Dificuldade"><input value={form.difficulty} onChange={(e) => setField("difficulty", e.target.value)} className="cec-input" /></Field>
        <Field label="Preço (€)"><input type="number" step="0.01" value={form.price} onChange={(e) => setField("price", parseFloat(e.target.value))} className="cec-input" /></Field>
        <Field label="Status">
          <select value={form.status} onChange={(e) => setField("status", e.target.value)} className="cec-input">
            <option value="available">Disponível</option>
            <option value="coming_soon">Em breve</option>
            <option value="sold_out">Esgotado</option>
          </select>
        </Field>
        <Field label="Data de lançamento (opcional)">
          <input value={form.release_date || ""} onChange={(e) => setField("release_date", e.target.value)} className="cec-input" />
        </Field>
        <Field label="Classificação etária">
          <input value={form.age_rating || ""} onChange={(e) => setField("age_rating", e.target.value)} className="cec-input" placeholder="14+" />
        </Field>
        <Field label="Nº de documentos no dossier">
          <input type="number" value={form.document_count || 0} onChange={(e) => setField("document_count", parseInt(e.target.value, 10) || 0)} className="cec-input" />
        </Field>
        <Field label="Feito em">
          <input value={form.made_in || ""} onChange={(e) => setField("made_in", e.target.value)} className="cec-input" placeholder="Portugal" />
        </Field>
        <Field label="Imagem de capa (URL)" wide>
          <input data-testid="admin-form-cover" value={form.cover_image} onChange={(e) => setField("cover_image", e.target.value)} className="cec-input" />
        </Field>
        <Field label="PDF do dossier (URL — entregue após compra)" wide>
          <input data-testid="admin-form-pdf" value={form.pdf_url || ""} onChange={(e) => setField("pdf_url", e.target.value)} className="cec-input" placeholder="https://..." />
        </Field>
        <Field label="Resumo curto" wide>
          <textarea value={form.summary} onChange={(e) => setField("summary", e.target.value)} className="cec-input min-h-[70px]" />
        </Field>
        <Field label="Descrição completa" wide>
          <textarea value={form.full_description} onChange={(e) => setField("full_description", e.target.value)} className="cec-input min-h-[120px]" />
        </Field>
        <Field label="Suspeitos (JSON array)" wide>
          <textarea defaultValue={JSON.stringify(form.suspects, null, 2)} onChange={(e) => setJsonField("suspects", e.target.value)} className="cec-input min-h-[120px] font-mono text-xs" />
        </Field>
        <Field label="Provas (JSON array)" wide>
          <textarea defaultValue={JSON.stringify(form.evidence, null, 2)} onChange={(e) => setJsonField("evidence", e.target.value)} className="cec-input min-h-[120px] font-mono text-xs" />
        </Field>
        <Field label="Pistas (JSON array)" wide>
          <textarea defaultValue={JSON.stringify(form.clues, null, 2)} onChange={(e) => setJsonField("clues", e.target.value)} className="cec-input min-h-[120px] font-mono text-xs" />
        </Field>
        <Field label="Ficha da Vítima (JSON — name, age, occupation, organization, description, image_url)" wide>
          <textarea defaultValue={JSON.stringify(form.victim || {}, null, 2)} onChange={(e) => setJsonField("victim", e.target.value)} className="cec-input min-h-[140px] font-mono text-xs" />
        </Field>
        <div className="md:col-span-2 flex items-center gap-3">
          <input type="checkbox" id="featured" checked={form.featured} onChange={(e) => setField("featured", e.target.checked)} />
          <label htmlFor="featured" className="mono text-xs tracking-[0.2em] text-bone-muted">EM DESTAQUE NA HOMEPAGE</label>
        </div>
      </div>

      <div className="flex gap-3 mt-7">
        <button onClick={() => onSave(form)} disabled={busy} data-testid="admin-form-save" className="btn-primary disabled:opacity-60">
          <Save className="w-4 h-4" /> {busy ? "A guardar..." : "Guardar"}
        </button>
        <button onClick={onCancel} className="btn-ghost">Cancelar</button>
      </div>

      <style>{`.cec-input { width:100%; background:#111; border:1px solid rgba(255,255,255,0.1); padding:10px 14px; font-family:'IBM Plex Sans', sans-serif; font-size:14px; color:#fff; transition:border-color .2s; }
      .cec-input:focus { outline:none; border-color:#C8102E; }`}</style>
    </div>
  );
}

const Field = ({ label, children, wide }) => (
  <div className={wide ? "md:col-span-2" : ""}>
    <label className="mono text-[10px] tracking-[0.3em] text-bone-faded block mb-2">{label.toUpperCase()}</label>
    {children}
  </div>
);
